import React, { useState, useEffect } from 'react'
import logoBase64 from '@/assests/shakthiLogo'
import {
  Table, Button, Space, Card, Form, Input, InputNumber, Select, Col,
  message, Alert, Tag, Tabs, Switch, Popconfirm, Tooltip, Drawer, Upload,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, FileTextOutlined,
  EyeOutlined, CopyOutlined, ReloadOutlined, PictureOutlined,
  FileAddOutlined, SaveOutlined, CheckCircleOutlined, StarOutlined, SearchOutlined,
} from '@ant-design/icons'
import { useMasterData, type ProposalTemplate } from '@/context/MasterDataContext'
import RichTextEditor from '@/components/RichTextEditor'
import { buildTemplateBody, getTemplateBody, getTemplateTermsHtml, buildDefaultTermsHtml, getCoverPage, getCompanyContent } from '@/utils/proposalTemplateBody'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { generateCode } from '@/utils/codeGenerator'

const BASE = '/master/proposal-templates'

/**
 * Structured defaults + standard clauses for a template. These are no longer
 * edited in the master UI (managers type the proposal body in the rich-text
 * editor instead), but they remain part of the data model — they drive the
 * proposal-form auto-fill, the list columns, and the default body generation.
 */
const DEFAULT_TEMPLATE_VALUES: Omit<
  ProposalTemplate,
  'id' | 'name' | 'description' | 'isDefault' | 'status' | 'bodyHtml'
> = {
  contractPeriod: 3,
  lockInPeriod: 2,
  noticePeriod: 6,
  commencementMonth: 'July 2026',
  validity: 30,
  commercialTerms: '5-6 Working Days',
  powerFacilitationCharge: 0.40,
  gstOnFacilitationCharge: 18,
  minimumAssuredSupply: '80 to 85%',
  rateRevision: '50-50% basis',
  billRaiseMethod: 'Based on TANGEDCO Energy Allocation statement',
  paymentDueInDays: 5,
  introParagraph:
    'Shakthi Power is a successful Power Consulting Company in Tamil Nadu with focus on "Third Party Power and Group Captive Power". Shakthi Power offers 250 kWh to any volume in single window turnkey solutions on Biomass, Coal, Solar & Wind Energy. With more than 10 years of experience and proficient workforce, we at Shakthi Power excel in complete turnkey in Power Trading enabling a smooth transaction with the Customers, Generators and TANGEDCO.\n\nWe hereby share our commercial offer for the quantum required by you and we would be very happy to hear from you for your suggestions.',
  billingClause: 'The monthly bill will be raised based on the Energy Allocation statement certified and issued by TANGEDCO.',
  paymentClause: 'Due date: Payment to be made within 5-6 working days from the date of Invoice.',
  paymentSecurityClause: 'Not Applicable',
  rateRevisionClause: 'TNEB base tariff and any open access charges revision will be shared between the Consumer and power supplier on a 50-50% basis.',
  minSupplyClause: '80 to 85% of the contracted quantum',
  requiredDocuments: [
    'Last 3 Months TNEB Bill copies, 0.2s calibration certificate & if required NOC from EDC',
    'PAN card copy, GST Registration, Incorporation certificate, AOA & MOA, Consent Letter & Authorisation letter',
  ],
  signingDocuments: [
    'Power Sale Agreement (PSA) / TERM Sheet',
    'Power Facilitation agreement',
  ],
  energyAdjustmentClauses: [
    'Peak hour generation shall be adjusted against peak hour consumption;',
    'Normal hour generation shall be adjusted against normal hour consumption and excess generation shall be adjusted against Off-peak hour consumption.',
    'After DSM implementation, the energy supply and adjustment will be on as per the relevant orders by TNERC.',
  ],
  validityNote: 'This offer is valid only till {VALIDITY_DATE}; after that, the availability of the power and the terms and prices are subject to change.',
  closingNote: 'Once again, thanks for the opportunity and looking forward to your confirmation to proceed further to enable the TANTRANSCO approvals.',
}

// The default rich-text body a new template starts with (manager edits freely)
const DEFAULT_BODY = buildTemplateBody(DEFAULT_TEMPLATE_VALUES as ProposalTemplate)

// The default rich-text Terms & Conditions a new template starts with
const DEFAULT_TERMS_HTML = buildDefaultTermsHtml(DEFAULT_TEMPLATE_VALUES as ProposalTemplate)

// {{placeholder}} tokens get their own color everywhere the body is shown —
// in the editor's live preview, the placeholder hint chips, and the full
// proposal preview — so managers can spot merge fields inside the prose at a glance.
const PLACEHOLDER_COLOR = '#0F9D6C'
const PLACEHOLDER_BG = '#E6F7EF'

const PlaceholderChip: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <code style={{
    color: PLACEHOLDER_COLOR, background: PLACEHOLDER_BG, fontWeight: 600,
    padding: '1px 6px', borderRadius: 4, fontSize: 12, ...style,
  }}>
    {children}
  </code>
)

// Wraps every {{token}} or {FIELD NAME} found as plain text in the body HTML
// with a colored span — safe because these tokens only ever appear as text
// content in generated/typed bodies, never inside a tag's markup.
const highlightPlaceholdersHtml = (html: string): string =>
  (html || '')
    .replace(/\{\{[^}]+\}\}/g, (m) => `<span class="tpl-placeholder">${m}</span>`)
    .replace(/\{[A-Z][A-Z0-9 _-]*\}/g, (m) => `<span class="tpl-placeholder">${m}</span>`)

// Commercial Terms tab fields, exposed as {FIELD NAME} placeholders in the
// Proposal Body / Terms & Conditions — mirrors the tokens proposalPdfGenerator
// resolves at download time. Order matches the Commercial Terms tab.
const COMMERCIAL_FIELD_PLACEHOLDERS: { label: string; field: keyof ProposalTemplate }[] = [
  { label: 'CONTRACT PERIOD',            field: 'contractPeriod' },
  { label: 'LOCK IN PERIOD',             field: 'lockInPeriod' },
  { label: 'NOTICE PERIOD',              field: 'noticePeriod' },
  { label: 'COMMENCEMENT MONTH',         field: 'commencementMonth' },
  { label: 'VALIDITY',                   field: 'validity' },
  { label: 'COMMERCIAL TERMS',           field: 'commercialTerms' },
  { label: 'POWER FACILITATION CHARGE',  field: 'powerFacilitationCharge' },
  { label: 'GST ON FACILITATION CHARGE', field: 'gstOnFacilitationCharge' },
  { label: 'PAYMENT DUE IN DAYS',        field: 'paymentDueInDays' },
  { label: 'MINIMUM ASSURED SUPPLY',     field: 'minimumAssuredSupply' },
  { label: 'RATE REVISION',              field: 'rateRevision' },
  { label: 'BILL RAISE METHOD',          field: 'billRaiseMethod' },
]

// Lightweight styling so the body preview resembles the generated PDF
const PREVIEW_STYLE = `
  .tpl-body-preview .intro{font-size:13px;text-align:justify;line-height:1.65;color:#333;margin-bottom:14px;}
  .tpl-body-preview .section-heading{font-size:13px;font-weight:bold;color:#001A4D;margin-bottom:8px;}
  .tpl-body-preview .pricing-note{font-size:12px;margin-bottom:8px;text-align:justify;color:#555;}
  .tpl-body-preview .facilitation{background:#fffbe6;border:1px solid #ffe58f;padding:8px 10px;font-size:12px;font-weight:600;margin-bottom:14px;}
  .tpl-body-preview .terms-title{font-size:13px;font-weight:bold;color:#001A4D;margin-bottom:10px;font-style:italic;}
  .tpl-body-preview .term-row{font-size:12px;margin-bottom:6px;display:flex;gap:8px;}
  .tpl-body-preview .term-label{font-weight:bold;min-width:140px;}
  .tpl-body-preview .sub-head{font-weight:bold;color:#00A651;font-size:12.5px;margin-top:12px;margin-bottom:4px;}
  .tpl-body-preview .bullet{font-size:12px;margin-left:16px;margin-bottom:4px;text-align:justify;line-height:1.5;}
  .tpl-body-preview .validity-box{background:#fffbe6;border:1px solid #ffe58f;padding:8px 10px;font-size:12px;font-weight:600;margin:14px 0;}
  .tpl-body-preview .closing{font-size:12.5px;margin-bottom:12px;text-align:justify;}
  .tpl-body-preview table{border-collapse:collapse;width:100%;font-size:11px;margin-bottom:8px;}
  .tpl-body-preview th,.tpl-body-preview td{border:1px solid #ccc;padding:6px 8px;text-align:center;}
  .tpl-placeholder{color:${PLACEHOLDER_COLOR};background:${PLACEHOLDER_BG};font-weight:600;padding:1px 4px;border-radius:4px;font-family:'JetBrains Mono',monospace;font-size:0.92em;}
`

// Replace placeholders with real values for the on-screen preview — the ones
// the template itself defines (PF charge, contract terms, …) resolve to the
// template's own live data; proposal-only ones (customer name/city, delivered
// tariff, …) fall back to readable sample text since no proposal exists yet.
const renderPreviewBody = (html: string, t: ProposalTemplate): string => {
  let out = html
    .replace(/\{\{\s*PRICING_TABLE\s*\}\}/g, '<div style="border:1px dashed #cbd5e1;background:#f8fafc;padding:10px;text-align:center;color:#64748b;font-size:12px;border-radius:6px;">[ Live pricing table is inserted here at download ]</div>')
    .replace(/\{\{\s*validityDate\s*\}\}/gi, '<u>DD-MM-YYYY</u>')
    .replace(/\{\{\s*customerName\s*\}\}/g, 'Customer Name')
    .replace(/\{\{\s*customerCity\s*\}\}/g, 'City')
    .replace(/\{\{\s*deliveredTariff\s*\}\}/g, 'X.XX')
    .replace(/\{\{\s*contractPeriod\s*\}\}/gi, String(t.contractPeriod ?? ''))
    .replace(/\{\{\s*lockInPeriod\s*\}\}/gi, String(t.lockInPeriod ?? ''))
    .replace(/\{\{\s*noticePeriod\s*\}\}/gi, String(t.noticePeriod ?? ''))
    .replace(/\{\{\s*commencementMonth\s*\}\}/gi, t.commencementMonth || '')
    .replace(/\{\{\s*powerFacilitationCharge\s*\}\}/gi, t.powerFacilitationCharge != null ? String(t.powerFacilitationCharge) : '')
    .replace(/\{\{\s*gstOnFacilitationCharge\s*\}\}/gi, String(t.gstOnFacilitationCharge ?? 18))
    .replace(/\{\{\s*commercialTerms\s*\}\}/gi, t.commercialTerms || '')
    .replace(/\{\{\s*validity\s*\}\}/gi, String(t.validity ?? ''))
    .replace(/\{\{\s*minimumAssuredSupply\s*\}\}/gi, t.minimumAssuredSupply || '')
    .replace(/\{\{\s*rateRevision\s*\}\}/gi, t.rateRevision || '')
    .replace(/\{\{\s*billRaiseMethod\s*\}\}/gi, t.billRaiseMethod || '')
    .replace(/\{\{\s*paymentDueInDays\s*\}\}/gi, String(t.paymentDueInDays ?? ''))

  // {FIELD NAME} form — same Commercial Terms values, template's own live data
  COMMERCIAL_FIELD_PLACEHOLDERS.forEach(({ label, field }) => {
    const value = t[field]
    out = out.replace(new RegExp(`\\{\\s*${label}\\s*\\}`, 'gi'), value != null ? String(value) : '')
  })

  // Any other placeholder isn't recognized — flag it instead of silently
  // blanking it out, so an unresolved/misspelled token stays visible.
  return out
    .replace(/\{\{[^}]+\}\}/g, (m) => `<span class="tpl-placeholder">${m}</span>`)
    .replace(/\{[A-Z][A-Z0-9 _-]*\}/g, (m) => `<span class="tpl-placeholder">${m}</span>`)
}

// Controlled base64 image picker for the cover-page background. Binds to an
// AntD <Form.Item> (value = base64 data URL, onChange = new value / '').
const CoverImageUpload: React.FC<{ value?: string; onChange?: (v: string) => void }> = ({ value, onChange }) => {
  const toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <Upload
        accept="image/*"
        showUploadList={false}
        beforeUpload={async (file) => {
          if (file.size > 2 * 1024 * 1024) {
            message.error('Image must be under 2 MB')
            return Upload.LIST_IGNORE
          }
          try {
            onChange?.(await toBase64(file))
          } catch {
            message.error('Could not read the image')
          }
          return false // handle locally, never upload to a server
        }}
      >
        <Button icon={<PictureOutlined />}>{value ? 'Change Image' : 'Upload Image'}</Button>
      </Upload>
      {value && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={value} alt="cover" style={{ height: 48, borderRadius: 4, border: '1px solid #e5e7eb' }} />
          <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={() => onChange?.('')}>
            Remove
          </Button>
        </div>
      )}
    </div>
  )
}

const ProposalTemplatesMaster: React.FC = () => {
  const { proposalTemplates: templates, setProposalTemplates: setTemplates, deleteProposalTemplate, saveProposalTemplate, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [previewTemplate, setPreviewTemplate] = useState<ProposalTemplate | null>(null)
  const [bodyEditorKey, setBodyEditorKey] = useState(0) // bump to remount CKEditor with fresh data
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  /* List search / filter */
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('')

  // Reset the rich-text body to the standard default letter
  const regenerateBody = () => {
    const src = mode === 'edit'
      ? (templates.find(t => t.id === editId) ?? (DEFAULT_TEMPLATE_VALUES as ProposalTemplate))
      : (DEFAULT_TEMPLATE_VALUES as ProposalTemplate)
    form.setFieldValue('bodyHtml', buildTemplateBody(src))
    setBodyEditorKey(k => k + 1)
    message.success('Body reset to the default letter')
  }

  // Reset the rich-text Terms & Conditions to the defaults derived from the clause fields
  const regenerateTerms = () => {
    const src = mode === 'edit'
      ? (templates.find(t => t.id === editId) ?? (DEFAULT_TEMPLATE_VALUES as ProposalTemplate))
      : (DEFAULT_TEMPLATE_VALUES as ProposalTemplate)
    form.setFieldValue('termsConditionsHtml', buildDefaultTermsHtml(src))
    setBodyEditorKey(k => k + 1)
    message.success('Terms & Conditions reset to the default clauses')
  }

  const activeCount = templates.filter(t => t.status === 'active').length

  const filteredTemplates = templates.filter(t => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      t.name.toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || t.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
      // New template starts with sensible defaults + the standard body, ready to edit
      form.setFieldsValue({
        ...DEFAULT_TEMPLATE_VALUES,
        status: 'active',
        isDefault: false,
        bodyHtml: DEFAULT_BODY,
        termsConditionsHtml: DEFAULT_TERMS_HTML,
      })
      setBodyEditorKey(k => k + 1)
    } else if (mode === 'edit') {
      const rec = templates.find(t => t.id === editId)
      if (rec) {
        // Show the manager's saved body + T&C, or the defaults derived from the clauses
        form.setFieldsValue({ ...rec, bodyHtml: getTemplateBody(rec), termsConditionsHtml: getTemplateTermsHtml(rec) })
        setBodyEditorKey(k => k + 1)
      } else goList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, templates])

  const onFinish = async (values: any) => {
    const isEdit = mode === 'edit'
    // Keep the hidden structured defaults so list columns + proposal auto-fill stay populated
    let id = isEdit ? editId! : ''
    if (!isEdit) {
      try { id = await generateCode('proposal_templates') } catch { id = genId('PT') }
    }
    const payload = isEdit ? { ...values, id } : { ...DEFAULT_TEMPLATE_VALUES, ...values, id }

    setSubmitting(true)
    try {
      await saveProposalTemplate(payload, isEdit)
      // If marking as default, unset others — a secondary, best-effort local
      // update persisted via the generic write-through sync, not this record.
      if (values.isDefault) {
        setTemplates(prev => prev.map(t => t.id === payload.id ? t : { ...t, isDefault: false }))
      }
      message.success(isEdit ? 'Template updated' : 'Template created')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const handleDuplicate = async (t: ProposalTemplate) => {
    const clone = { ...t, id: genId('PT'), name: `${t.name} (Copy)`, isDefault: false }
    try {
      await saveProposalTemplate(clone, false)
      message.success('Template duplicated')
    } catch { /* error already shown */ }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteProposalTemplate(id)
      message.success('Template deleted')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'name',
      render: (v: string, r: ProposalTemplate) => (
        <div>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#1677ff' }}>
            {v}
          </span>
          {r.isDefault && (
            <Tag color="gold" style={{ marginLeft: 8, fontSize: 10 }}>Default</Tag>
          )}
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{r.description}</div>
        </div>
      ),
    },
    {
      title: 'Contract',
      width: 130,
      render: (_: any, r: ProposalTemplate) => (
        <div style={{ fontSize: 12, color: '#475569' }}>
          <div>{r.contractPeriod}yr / Lock-in {r.lockInPeriod}yr</div>
          <div style={{ color: '#94a3b8' }}>Notice {r.noticePeriod}mo</div>
        </div>
      ),
    },
    {
      title: 'Validity',
      dataIndex: 'validity',
      width: 90,
      render: (v: number) => <Tag color="blue">{v} days</Tag>,
    },
    {
      title: 'PF Charge',
      width: 110,
      render: (_: any, r: ProposalTemplate) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
          ₹{r.powerFacilitationCharge}/{r.gstOnFacilitationCharge}% GST
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => (
        <Tag color={s === 'active' ? 'green' : 'default'}>
          {s === 'active' ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: ProposalTemplate) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setPreviewTemplate(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ─────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {mode === 'edit' ? <EditOutlined style={{ color: '#001A4D' }} /> : <FileAddOutlined style={{ color: '#001A4D' }} />}
            {mode === 'edit' ? 'Edit Proposal Template' : 'New Proposal Template'}
          </span>
        }
        subtitle="Proposal template — body and terms & conditions"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ marginTop: 12 }}>
          <Tabs
            items={[
              // ── Tab 1: Basic Info ────────────────────────────────
              {
                key: 'basic',
                label: '📋 Basic Info',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                      <Form.Item name="name" label="Template Name" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Shakthi Power – Wind & Solar" />
                      </Form.Item>
                      <Form.Item name="status" label="Status" initialValue="active">
                        <Select options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
                      </Form.Item>
                    </div>
                    <Form.Item name="description" label="Short Description (shown in dropdown)">
                      <Input placeholder="e.g. Standard Wind & Solar offer with PF charges" />
                    </Form.Item>
                    <Form.Item name="isDefault" label="Set as Default Template" valuePropName="checked">
                      <Switch checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                  </div>
                ),
              },
              // ── Tab 2: Commercial & Contract Terms ───────────────
              // These are the defaults every new Proposal auto-fills from
              // (contract period, PF charge, turnaround time, …) — they used
              // to have no Form.Item here at all, so editing/saving a
              // template could never actually change them; only the
              // hardcoded creation-time defaults ever applied.
              {
                key: 'commercial',
                label: '💰 Commercial Terms',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 14 }}
                      message="These values are pre-filled into every new Proposal that uses this template."
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
                      <Form.Item name="contractPeriod" label="Contract Period (years)" rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name="lockInPeriod" label="Lock-in Period (years)" rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name="noticePeriod" label="Notice Period (months)" rules={[{ required: true }]}>
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name="commencementMonth" label="Commencement Month">
                        <Input placeholder="e.g. July 2026" />
                      </Form.Item>
                      <Form.Item name="validity" label="Proposal Validity (days)" rules={[{ required: true }]}>
                        <Select options={[{ value: 15, label: '15 days' }, { value: 30, label: '30 days' }]} />
                      </Form.Item>
                      <Form.Item name="gstOnFacilitationCharge" label="GST on Facilitation Charge (%)">
                        <InputNumber min={0} max={100} style={{ width: '100%' }} addonAfter="%" />
                      </Form.Item>
                      <Form.Item name="paymentDueInDays" label="Payment Due (days)">
                        <InputNumber min={0} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name="minimumAssuredSupply" label="Minimum Assured Supply">
                        <Input placeholder="e.g. 80 to 85%" />
                      </Form.Item>
                      <Form.Item name="rateRevision" label="Rate Revision Basis">
                        <Input placeholder="e.g. 50-50% basis" />
                      </Form.Item>
                      <Form.Item name="billRaiseMethod" label="Bill Raise Method">
                        <Input placeholder="e.g. Based on TANGEDCO Energy Allocation statement" />
                      </Form.Item>
                    </div>
                  </div>
                ),
              },
              // ── Tab 3: Cover Page (page 1 of the PDF) ────────────
              {
                key: 'cover',
                label: '🖼️ Cover Page',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 14 }}
                      message="The cover page is page 1 of the generated proposal — shown before the company content and the letter."
                      description={<span style={{ fontSize: 12 }}>Turn it on and set a title/subtitle. The company logo, customer name, reference number and date are added automatically.</span>}
                    />
                    <Form.Item name="coverPageEnabled" label="Include Cover Page" valuePropName="checked">
                      <Switch checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                      <Form.Item name="coverTitle" label="Cover Title">
                        <Input placeholder="e.g. Commercial Proposal" />
                      </Form.Item>
                      <Form.Item name="coverSubtitle" label="Cover Subtitle / Tagline">
                        <Input placeholder="e.g. Third Party & Group Captive Power" />
                      </Form.Item>
                    </div>
                    <Form.Item name="coverDescription" label="Cover Description (optional paragraph under the subtitle)">
                      <Input.TextArea
                        autoSize={{ minRows: 3, maxRows: 8 }}
                        placeholder="e.g. A brief introduction to Shakthi Power and this commercial proposal for your power requirements…"
                      />
                    </Form.Item>
                    <Form.Item name="coverImage" label="Cover Background Image (optional, faint watermark)">
                      <CoverImageUpload />
                    </Form.Item>
                  </div>
                ),
              },
              // ── Tab 4: Company Content (page 2 of the PDF) ───────
              {
                key: 'company',
                label: '🏢 Company Content',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="A company profile / “About Us” page shown as page 2 — after the cover page and before the proposal letter."
                      description={<span style={{ fontSize: 12 }}>Add text and images freely. Leave empty to skip this page entirely.</span>}
                    />
                    <Form.Item name="companyContentHtml" valuePropName="value" trigger="onChange" style={{ marginBottom: 0 }}>
                      <RichTextEditor
                        key={`company-${bodyEditorKey}`}
                        placeholder="Company profile / About Us — add text and images…"
                        minHeight={360}
                      />
                    </Form.Item>
                  </div>
                ),
              },
              // ── Tab 6: Proposal Body (Rich Text / CKEditor) ──────
              {
                key: 'body',
                label: '📝 Proposal Body',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Type the proposal body freely with formatting — this is what appears in the generated PDF."
                      description={
                        <span style={{ fontSize: 12 }}>
                          Use these placeholders and they get filled at download time (shown in{' '}
                          <span style={{ color: PLACEHOLDER_COLOR, fontWeight: 600 }}>green</span> below so they're easy to spot in the body). Type a number/text directly and it goes stale
                          the moment that field is edited — use the matching placeholder instead so the letter always tracks the current value:
                          <br /><br />
                          <strong>Customer / proposal:</strong>{' '}
                          <PlaceholderChip>{'{{PRICING_TABLE}}'}</PlaceholderChip>{' '}
                          <PlaceholderChip>{'{{validityDate}}'}</PlaceholderChip>{' '}
                          <PlaceholderChip>{'{{customerName}}'}</PlaceholderChip>{' '}
                          <PlaceholderChip>{'{{customerCity}}'}</PlaceholderChip>{' '}
                          <PlaceholderChip>{'{{deliveredTariff}}'}</PlaceholderChip>
                          <br /><br />
                          <strong>Commercial Terms tab (this template's own values):</strong>{' '}
                          {COMMERCIAL_FIELD_PLACEHOLDERS.map(({ label }) => (
                            <PlaceholderChip key={label} style={{ marginRight: 4, marginBottom: 4, display: 'inline-block' }}>{`{${label}}`}</PlaceholderChip>
                          ))}
                        </span>
                      }
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <Tooltip title="Replace the body with the standard default proposal letter">
                        <Button size="small" icon={<ReloadOutlined />} onClick={regenerateBody}>
                          Reset to default letter
                        </Button>
                      </Tooltip>
                    </div>
                    <Form.Item name="bodyHtml" valuePropName="value" trigger="onChange" style={{ marginBottom: 0 }}>
                      <RichTextEditor
                        key={bodyEditorKey}
                        placeholder="Type the full proposal body here — managers can write whatever they want…"
                        minHeight={360}
                      />
                    </Form.Item>

                    {/* Live echo of the body with {{placeholders}} colored, so
                        they're easy to pick out from the surrounding prose
                        while composing — the editor itself can't color
                        partial text as you type. */}
                    <style>{PREVIEW_STYLE}</style>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#8A93A6', letterSpacing: 0.5, margin: '16px 0 8px' }}>
                      Placeholder Preview
                    </div>
                    <Form.Item shouldUpdate noStyle>
                      {() => (
                        <div
                          className="tpl-body-preview"
                          style={{ border: '1px solid #E4E8F0', borderRadius: 8, padding: '16px 20px', background: '#FAFBFD' }}
                          dangerouslySetInnerHTML={{ __html: highlightPlaceholdersHtml(form.getFieldValue('bodyHtml') || '') }}
                        />
                      )}
                    </Form.Item>
                  </div>
                ),
              },
              // ── Tab 7: Terms & Conditions ────────────────────────
              {
                key: 'terms',
                label: '📜 Terms & Conditions',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 14 }}
                      message="Type the Terms & Conditions freely with formatting — offered on every proposal created from this template (managers can override it per proposal)."
                      description={
                        <span style={{ fontSize: 12 }}>
                          Use these placeholders and they get filled at download time — the letter always tracks the current value instead of going stale:
                          <br /><br />
                          {COMMERCIAL_FIELD_PLACEHOLDERS.map(({ label }) => (
                            <PlaceholderChip key={label} style={{ marginRight: 4, marginBottom: 4, display: 'inline-block' }}>{`{${label}}`}</PlaceholderChip>
                          ))}
                        </span>
                      }
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <Tooltip title="Replace with the default clauses derived from the Commercial Terms tab">
                        <Button size="small" icon={<ReloadOutlined />} onClick={regenerateTerms}>
                          Reset to default clauses
                        </Button>
                      </Tooltip>
                    </div>
                    <Form.Item name="termsConditionsHtml" valuePropName="value" trigger="onChange" style={{ marginBottom: 0 }}>
                      <RichTextEditor
                        key={`terms-${bodyEditorKey}`}
                        placeholder="Type the Terms & Conditions here…"
                        minHeight={320}
                      />
                    </Form.Item>

                    {/* Live echo of the T&C with {placeholders} colored, so
                        they're easy to pick out from the surrounding prose
                        while composing — the editor itself can't color
                        partial text as you type. */}
                    <style>{PREVIEW_STYLE}</style>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#8A93A6', letterSpacing: 0.5, margin: '16px 0 8px' }}>
                      Placeholder Preview
                    </div>
                    <Form.Item shouldUpdate noStyle>
                      {() => (
                        <div
                          className="tpl-body-preview"
                          style={{ border: '1px solid #E4E8F0', borderRadius: 8, padding: '16px 20px', background: '#FAFBFD' }}
                          dangerouslySetInnerHTML={{ __html: highlightPlaceholdersHtml(form.getFieldValue('termsConditionsHtml') || '') }}
                        />
                      )}
                    </Form.Item>
                  </div>
                ),
              },
            ]}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" icon={mode === 'edit' ? <SaveOutlined /> : <FileAddOutlined />} loading={submitting}>
              {mode === 'edit' ? 'Save Changes' : 'Create Template'}
            </Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={5} />

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileTextOutlined style={{ color: '#001A4D' }} /> Proposal Templates Master
          </h2>
          <p className="page-subtitle">
            Configure proposal templates — each template defines the Terms & Conditions
            and default contract values used when generating proposals.
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>
          New Template
        </Button>
      </div>

      {/* ── Stat cards ── */}
      <KpiCardRow
        cards={[
          { label: 'Total Templates', value: templates.length, icon: <FileTextOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Default Set', value: templates.some(t => t.isDefault && t.status === 'active') ? 'Yes' : 'No', icon: <StarOutlined style={{ fontSize: 24, color: '#F59E0B' }} />, iconBg: 'rgba(245,158,11,0.08)', borderColor: '#F59E0B' },
        ]}
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, borderRadius: 8 }}
        message="Each template has a Proposal Body (rich text) that managers can edit freely. When a proposal is downloaded, the chosen template's body is used — or the Default template's body if none is chosen."
      />

      {/* ── Filter bar ── */}
      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by name or description..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredTemplates}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} templates` }}
        />
      </Card>

      {/* ── Preview Modal ────────────────────────────────────────── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>📄 Preview — {previewTemplate?.name}</div>
            <div style={{ fontSize: 12, color: '#8A93A6' }}>{previewTemplate?.description}</div>
          </div>
        }
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        width={680}
        extra={
          <Space>
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined />} onClick={() => { if (previewTemplate) goEdit(previewTemplate.id); setPreviewTemplate(null) }} />
            </Tooltip>
            <Tooltip title="Duplicate">
              <Button type="text" icon={<CopyOutlined />} onClick={() => { handleDuplicate(previewTemplate!); setPreviewTemplate(null) }} />
            </Tooltip>
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this record?"
                description="This action cannot be undone."
                onConfirm={() => { handleDelete(previewTemplate!.id); setPreviewTemplate(null) }}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </Space>
        }
      >
        {previewTemplate && (
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <style>{PREVIEW_STYLE}</style>
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
              <strong>Contract:</strong> {previewTemplate.contractPeriod}yr &nbsp;|&nbsp;
              <strong>Lock-in:</strong> {previewTemplate.lockInPeriod}yr &nbsp;|&nbsp;
              <strong>Notice:</strong> {previewTemplate.noticePeriod}mo &nbsp;|&nbsp;
              <strong>Validity:</strong> {previewTemplate.validity} days
            </div>

            {/* Page 1 — Cover page (only when enabled) */}
            {(() => {
              const cover = getCoverPage(previewTemplate)
              const bgImg = cover?.image || 'https://images.unsplash.com/photo-1509391365360-2e959784a276?q=80&w=1000&auto=format&fit=crop'
              return cover ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>PAGE 1 — COVER</div>
                  <div style={{
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    height: 360,
                    display: 'flex',
                    background: '#fff'
                  }}>
                    <div style={{
                      width: '60%',
                      height: '100%',
                      backgroundImage: `url(${bgImg})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <div style={{
                        width: '100%',
                        background: 'rgba(255,255,255,0.92)',
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}>
                        <img src={logoBase64} alt="Logo" style={{ height: 26, width: 'auto' }} />
                        <span style={{ fontSize: 10, fontWeight: 800, color: '#00A651', textTransform: 'uppercase', textAlign: 'right' }}>
                          {cover.subtitle || 'SOLAR ENERGY FOR A BETTER TOMORROW'}
                        </span>
                      </div>
                    </div>
                    <div style={{
                      width: '40%',
                      height: '100%',
                      background: 'linear-gradient(180deg, #F59E0B 0%, #D97706 100%)',
                      padding: '24px 16px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      color: '#000',
                      boxSizing: 'border-box'
                    }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#001A4D', marginBottom: 8, textTransform: 'uppercase' }}>{cover.title}</div>
                        <div style={{ fontSize: 10.5, color: '#1e293b', lineHeight: 1.6 }}>
                          <div><strong>Prepared For:</strong> [Customer Name]</div>
                          <div><strong>Ref No:</strong> [Ref Number]</div>
                          <div><strong>Date:</strong> [Date]</div>
                        </div>
                      </div>
                      <div>
                        <div style={{ height: 2, background: '#000', width: '100%', marginBottom: 8 }} />
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#000', lineHeight: 1.4 }}>
                          {cover.description ? cover.description : (
                            <>
                              <div>Maniarasan Rajavel</div>
                              <div style={{ fontWeight: 600 }}>Grand Shakthi Power P Ltd</div>
                              <div style={{ fontStyle: 'italic', fontWeight: 500, fontSize: 9.5 }}>maniarasan@shakthipower.com</div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null
            })()}

            {/* Page 2 — Company content (only when set) */}
            {(() => {
              const company = getCompanyContent(previewTemplate)
              return company ? (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>PAGE 2 — COMPANY CONTENT</div>
                  <div
                    style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px' }}
                    dangerouslySetInnerHTML={{ __html: company }}
                  />
                </div>
              ) : null
            })()}

            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>PROPOSAL LETTER</div>
            <div
              className="tpl-body-preview"
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px' }}
              dangerouslySetInnerHTML={{ __html: renderPreviewBody(getTemplateBody(previewTemplate), previewTemplate) }}
            />

            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', margin: '16px 0 6px' }}>TERMS &amp; CONDITIONS</div>
            <div
              className="tpl-body-preview"
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '16px 20px' }}
              dangerouslySetInnerHTML={{ __html: renderPreviewBody(getTemplateTermsHtml(previewTemplate), previewTemplate) }}
            />
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default ProposalTemplatesMaster
